import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { shiftApi } from '../../../api/shiftApi';
import { toast } from 'react-toastify';
import { formatTime12Hour, getTimeMinutes } from '../../../utils/timeUtils';
import ShiftForm from './ShiftForm';

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

interface Shift {
  id: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  breakDuration: number;
  graceTime: number;
  isActive: boolean;
  isAssigned?: boolean;
}

type ViewAllShiftsProps = {
  refreshKey?: number;
};

const ViewAllShifts: React.FC<ViewAllShiftsProps> = ({ refreshKey }) => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [search, setSearch] = useState('');

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await shiftApi.getShifts();
      setShifts(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts, refreshKey]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;
    try {
      await shiftApi.deleteShift(id);
      toast.success('Shift deleted successfully');
      fetchShifts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete shift');
    }
  };

  const handleEditSuccess = async () => {
    setEditingShift(null);
    await fetchShifts();
  };

  const isNightShift = (shift: Shift): boolean => {
    const startMinutes = getTimeMinutes(shift.startTime);
    const endMinutes = getTimeMinutes(shift.endTime);

    if (startMinutes === null || endMinutes === null) {
      return shift.endTime < shift.startTime;
    }

    return endMinutes < startMinutes;
  };

  const filteredShifts = shifts.filter((shift) =>
    shift.shiftName.toLowerCase().includes(search.trim().toLowerCase())
  );

  const paginatedShifts = rowsPerPage === -1
    ? filteredShifts
    : filteredShifts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          All Shifts
        </Typography>
        <TextField
          size="small"
          label="Search by Shift Name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 260 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
      <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Shift Name</strong></TableCell>
              <TableCell><strong>Start Time</strong></TableCell>
              <TableCell><strong>End Time</strong></TableCell>
              <TableCell><strong>Break Duration</strong></TableCell>
              <TableCell><strong>Grace Period</strong></TableCell>
              <TableCell><strong>Night Shift</strong></TableCell>
              <TableCell><strong>Assigned</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedShifts.map((shift) => (
              <TableRow key={shift.id} hover>
                <TableCell>{shift.shiftName}</TableCell>
                <TableCell>{formatTime12Hour(shift.startTime)}</TableCell>
                <TableCell>{formatTime12Hour(shift.endTime)}</TableCell>
                <TableCell>{shift.breakDuration} min</TableCell>
                <TableCell>{shift.graceTime} min</TableCell>
                <TableCell>{isNightShift(shift) ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Chip
                    label={shift.isAssigned ? 'Yes' : 'No'}
                    color={shift.isAssigned ? 'info' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={shift.isActive ? 'Active' : 'Inactive'}
                    color={shift.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => setEditingShift(shift)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(shift.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredShifts.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
      <Dialog open={!!editingShift} onClose={() => setEditingShift(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Shift</DialogTitle>
        <DialogContent>
          {editingShift && (
            <ShiftForm
              showCard={false}
              showTitle={false}
              initialShift={editingShift}
              timeFieldsDisabled={Boolean(editingShift.isAssigned)}
              onSuccess={handleEditSuccess}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingShift(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

export default ViewAllShifts;
